import type { ConditionNode } from "@/lib/api"
import { cn } from "@/lib/utils"

/**
 * Дерево условия правила — как оно лежит в каталоге, без пересказа.
 *
 * Операторы печатаются своими именами (`gte`, `contains`, `is_not_null`), а не
 * переводятся в «больше или равно»: ровно эти имена аналитик увидит в YAML
 * каталога и в ответе ручки, и третьего словаря между ними быть не должно.
 *
 * Логических операторов ровно три (`backend/scoring/conditions.py`); всё
 * остальное — лист с полем, оператором и значением.
 */
const LOGICAL_OPS = ["and", "or", "not"] as const

function isLogical(op: string) {
  return (LOGICAL_OPS as readonly string[]).includes(op)
}

/**
 * Значение условия в печатном виде.
 *
 * Строки — в кавычках намеренно: без них `"+"` в условии `contains` выглядит
 * как опечатка вёрстки, а пробел на краю значения не виден вовсе.
 */
function printValue(value: unknown): string {
  if (value === null || value === undefined) return "null"
  if (typeof value === "string") return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(printValue).join(", ")}]`
  return String(value)
}

export function ConditionTree({ node, depth = 0 }: { node: ConditionNode; depth?: number }) {
  if (isLogical(node.op)) {
    const args = node.args ?? []
    return (
      <div className={cn(depth > 0 && "mt-1.5")}>
        <span className="text-xs font-semibold uppercase tracking-[0.04em] text-accent">
          {node.op}
        </span>
        <div className="mt-1 space-y-1.5 border-l border-line-strong pl-3">
          {args.map((child, index) => (
            <ConditionTree key={index} node={child} depth={depth + 1} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 font-mono text-xs">
      {/*
       * Поле печатаем, только если оно есть. У локального правила чёрного списка
       * условие — один оператор без поля (`{"op": "in_blacklist"}`), и пустая
       * плашка рисовалась серым огрызком перед именем оператора.
       */}
      {node.field && (
        <span className="rounded-sm bg-surface-sunken px-1.5 py-0.5 text-fg">{node.field}</span>
      )}
      <span className="text-accent">{node.op}</span>
      {node.value !== undefined && node.value !== null && (
        <span className="text-fg-muted">{printValue(node.value)}</span>
      )}
    </div>
  )
}

/**
 * Тон статуса правила. Цвет — единственное, что фронт добавляет к словарю
 * движка: сам статус печатается как есть (`skipped_no_data`), поэтому
 * разойтись с бэкендом здесь нечему.
 *
 * `matched` красный, а не зелёный: сработавшее правило добавляет баллы риска —
 * так же, как красится `+85` в колонке ACTION.
 */
export function statusTone(status: string): "neutral" | "approve" | "review" | "decline" {
  if (status === "matched") return "decline"
  if (status === "not_matched") return "neutral"
  return "review"
}
